import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Smartphone, Globe, Camera, PenTool, Code, Share2, Search, BarChart, Zap, ArrowRight } from 'lucide-react';
import Layout from '../components/Layout';
import PageBanner from '../components/Banner';
import { useNavigate } from 'react-router-dom';

const ACCENT = '#E8611A';
const CARD_ACCENTS = [ACCENT, '#3b82f6', '#16a34a', '#7c3aed', '#0891b2', '#f59e0b'];

const ServiceCard = ({ icon: Icon, title, description, features, accent }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: '#fff', borderRadius: 16, padding: '24px 22px',
        border: hovered ? `1.5px solid ${accent}40` : '1.5px solid rgba(0,0,0,0.07)',
        boxShadow: hovered ? '0 12px 36px rgba(0,0,0,0.10)' : '0 2px 10px rgba(0,0,0,0.05)',
        transition: 'box-shadow .25s, border-color .25s, transform .25s',
        transform: hovered ? 'translateY(-4px)' : 'none',
        fontFamily: "'DM Sans',sans-serif",
      }}
    >
      <div style={{ width: 48, height: 48, borderRadius: 12, background: hovered ? accent : `${accent}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16, transition: 'background .25s' }}>
        <Icon size={22} color={hovered ? '#fff' : accent} />
      </div>
      <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#1e293b', margin: '0 0 6px' }}>{title}</h3>
      <p style={{ fontSize: '0.83rem', color: '#64748b', margin: '0 0 14px', lineHeight: 1.6 }}>{description}</p>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 7 }}>
        {features.map((f, i) => (
          <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.81rem', color: '#475569' }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: accent, flexShrink: 0 }} />
            {f}
          </li>
        ))}
      </ul>
    </div>
  );
};

const WebMedia = () => {
  const navigate = useNavigate();
  const services = [
    {
      icon: Globe,
      title: "Création de Sites Web",
      description: "Des sites web modernes, performants et adaptés à vos besoins",
      features: [
        "Sites vitrine & e-commerce",
        "Design responsive & moderne",
        "Optimisation SEO",
        "Expérience utilisateur optimale"
      ]
    },
    {
      icon: PenTool,
      title: "Design & Identité Visuelle",
      description: "Création d'identités visuelles uniques et mémorables",
      features: [
        "Logo & charte graphique",
        "Supports de communication",
        "UI/UX Design",
        "Design d'applications"
      ]
    },
    {
      icon: Share2,
      title: "Marketing Digital",
      description: "Stratégies digitales pour augmenter votre visibilité",
      features: [
        "Stratégie social media",
        "Campagnes publicitaires",
        "Content marketing",
        "Email marketing"
      ]
    },
    {
      icon: Camera,
      title: "Production Multimédia",
      description: "Contenu multimédia professionnel pour tous vos besoins",
      features: [
        "Photos professionnelles",
        "Vidéos promotionnelles",
        "Motion design",
        "Podcasts & audio"
      ]
    },
    {
      icon: Smartphone,
      title: "Applications Mobiles",
      description: "Développement d'applications mobiles performantes",
      features: [
        "iOS & Android",
        "Applications natives",
        "Applications hybrides",
        "PWA"
      ]
    },
    {
      icon: BarChart,
      title: "Analytics & SEO",
      description: "Optimisation de votre présence digitale",
      features: [
        "Audit SEO",
        "Analyse de données",
        "Optimisation conversion",
        "Rapports personnalisés"
      ]
    }
  ];



  return (
    <Layout>
      <Helmet>
        <title>Développement de Solutions Web & Mobile - Aydees | Niger</title>
        <meta name="description" content="Aydees : création de sites web, applications mobiles, design graphique, marketing digital et production multimédia au Niger." />
        <meta name="keywords" content="agence web, création site web, application mobile, design graphique, marketing digital, Aydees, Niger" />
      </Helmet>

      <PageBanner
        title="Développement de Solutions Web & Mobile"
        subtitle="Créez votre présence digitale avec une agence créative et innovante"
      />

      {/* Services */}
      <section style={{ background: 'linear-gradient(135deg,#fff7ed 0%,#ffffff 60%,#f8fafc 100%)', padding: '64px 24px', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, opacity: 0.35, pointerEvents: 'none', backgroundImage: 'radial-gradient(circle,#e2e8f0 1px,transparent 1px)', backgroundSize: '28px 28px' }} />
        <div style={{ maxWidth: 1100, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '5px 16px', borderRadius: 999, border: `1px solid ${ACCENT}30`, background: `${ACCENT}0d`, marginBottom: 12 }}>
              <span style={{ fontSize: '0.68rem', fontWeight: 700, color: ACCENT, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Nos services</span>
            </div>
            <h2 style={{ fontSize: 'clamp(1.4rem,2.5vw,1.9rem)', fontWeight: 800, color: '#1e293b', margin: '0 0 8px', fontFamily: "'DM Sans',sans-serif" }}>Solutions digitales complètes</h2>
            <p style={{ color: '#64748b', fontSize: '0.9rem', maxWidth: 500, margin: '0 auto', fontFamily: "'DM Sans',sans-serif" }}>Pour votre présence en ligne et votre transformation numérique</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(300px,1fr))', gap: 20 }}>
            {services.map((service, i) => (
              <ServiceCard key={i} {...service} accent={CARD_ACCENTS[i % CARD_ACCENTS.length]} />
            ))}
          </div>
        </div>
      </section>

      {/* Processus */}
      <section style={{ background: '#f8fafc', padding: '64px 24px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <h2 style={{ fontSize: 'clamp(1.4rem,2.5vw,1.9rem)', fontWeight: 800, color: '#1e293b', margin: '0 0 8px', fontFamily: "'DM Sans',sans-serif" }}>Notre Processus</h2>
            <p style={{ color: '#64748b', fontSize: '0.9rem', maxWidth: 500, margin: '0 auto', fontFamily: "'DM Sans',sans-serif" }}>Une approche structurée pour des projets réussis</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))', gap: 20 }}>
            {[
              { num: '01', icon: Search,  title: 'Découverte',    desc: 'Analyse de vos besoins et objectifs' },
              { num: '02', icon: PenTool, title: 'Conception',    desc: 'Design et planification détaillée' },
              { num: '03', icon: Code,    title: 'Développement', desc: 'Création et intégration technique' },
              { num: '04', icon: Zap,     title: 'Déploiement',   desc: 'Tests rigoureux et mise en ligne' },
            ].map((step, i) => {
              const ac = CARD_ACCENTS[i];
              return (
                <div key={i} style={{ background: '#fff', borderRadius: 16, padding: '24px 20px', border: '1.5px solid rgba(0,0,0,0.06)', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', fontFamily: "'DM Sans',sans-serif" }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                    <div style={{ width: 40, height: 40, borderRadius: 10, background: ac, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <step.icon size={18} color="#fff" />
                    </div>
                    <span style={{ fontSize: '1.4rem', fontWeight: 900, color: `${ac}30`, lineHeight: 1 }}>{step.num}</span>
                  </div>
                  <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#1e293b', margin: '0 0 6px' }}>{step.title}</h3>
                  <p style={{ fontSize: '0.82rem', color: '#64748b', margin: 0, lineHeight: 1.6 }}>{step.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ background: 'linear-gradient(135deg,#0f172a 0%,#1e293b 100%)', padding: '64px 24px', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, background: `${ACCENT}12`, pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: '-20%', left: '5%', width: 340, height: 340, borderRadius: '50%', background: `radial-gradient(circle,${ACCENT}20 0%,transparent 70%)`, filter: 'blur(60px)', pointerEvents: 'none' }} />
        <div style={{ maxWidth: 640, margin: '0 auto', textAlign: 'center', position: 'relative', zIndex: 1, fontFamily: "'DM Sans',sans-serif" }}>
          <h2 style={{ fontSize: 'clamp(1.4rem,2.5vw,2rem)', fontWeight: 800, color: '#fff', margin: '0 0 12px' }}>Prêt à lancer votre projet digital ?</h2>
          <p style={{ color: '#94a3b8', fontSize: '0.95rem', margin: '0 0 28px', lineHeight: 1.7 }}>Contactez-nous pour discuter de votre projet et obtenir un devis personnalisé.</p>
          <button
            onClick={() => navigate('/rejoignez-nous')}
            onMouseEnter={e => { e.currentTarget.style.background = '#d4501a'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = ACCENT; e.currentTarget.style.transform = 'none'; }}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '13px 28px', borderRadius: 10, background: ACCENT, color: '#fff', fontWeight: 700, fontSize: '0.9rem', border: 'none', cursor: 'pointer', transition: 'background .2s, transform .2s', boxShadow: `0 6px 20px ${ACCENT}40` }}
          >
            Démarrer votre projet <ArrowRight size={17} />
          </button>
        </div>
      </section>
    </Layout>
  );
};

export default WebMedia;